import { useLayoutEffect, useRef, useState } from 'react'
import { ChevronLeft, X } from 'lucide-react'
import { useScreenHeaderStore, type ScreenHeaderTools } from '../stores/screenHeaderStore'
import {
  SCREEN_HEADER_CONTROLS_GAP,
  SCREEN_HEADER_GAP,
  SCREEN_HEADER_NAME_MIN_WIDTH,
  SCREEN_HEADER_NAME_NARROW_FLOOR,
  SCREEN_HEADER_UPGRADE_HYSTERESIS,
  SCREEN_HEADER_ZONE_GAP,
} from '../utils/screenHeader'

type ToolsTier = 'full' | 'compact' | 'overflow'

/**
 * Desktop-only per-screen header bar (DESIGN_SYSTEM.md §8 for its plane and
 * text, §10 for the distribution below).
 *
 * **THE BAR HAS TWO ENDS AND SLACK BETWEEN THEM.** Not four items in a row:
 *
 *   [ back · name · chip ] [ ←—— slack ——→ ] [ action · overflow ]
 *
 *  1. **Identity flush left**, and NOTHING sits immediately after the name.
 *     Adjacency reads as apposition — a deck's name with a control beside it
 *     scans as one phrase ("Band verification deck · Edit every card"), which
 *     is what this distribution exists to stop.
 *  2. **The screen's controls form one group flush right**, ordered
 *     least-persistent first: the action, then the overflow. The overflow
 *     leaving the identity cluster is the fix, and it is also what finally
 *     gives the name a right edge to truncate against.
 *  3. **The slack is the only thing that stretches.** It is a real element
 *     (`flex: 1 1 auto`), not free space left over by a `margin-left: auto` on
 *     the right group: a screen's own navigation centres INSIDE it, and a
 *     field CONSUMES it (Library), so it has to be somewhere a screen's
 *     content can be put rather than an absence.
 *  4. **A screen with no action leaves the slot empty and it collapses** — a
 *     system deck has no mode at all, and the bar does not reserve space for a
 *     control the screen never renders.
 *
 * **The hairline that is not drawn.** §10.4 closes the screen's zone with a
 * `--header-rule` hairline *before the app's own zone* (plan badge, avatar) —
 * different owner, and without the rule the app's zone reads as the last of
 * the screen's controls. This app has no such zone in the bar: the plan badge
 * and the avatar live in the sidebar, so the screen's zone runs to the bar's
 * own right inset and a hairline there would close it against nothing. That is
 * not an omission, it is §10.8's rule applied — space (or a mark) reserved for
 * a control the screen does not render is exactly the defect that put a dead
 * hairline a padding-width short of the true edge here once already (erratum,
 * 2026-09-10). The token is declared and the rule stands; the day an app zone
 * lands in this bar, the hairline lands with it.
 *
 * **The name's floor, and what gives before it does.** The identity zone is
 * the one part of the row that shrinks. `SCREEN_HEADER_NAME_MIN_WIDTH` is not
 * set as a CSS `min-width` — that would reserve the floor even for a short
 * title like "Home" and strand the controls away from the row's edge. It is
 * upheld by the fit check below, which steps a LADDER of the row's own chrome
 * down, in order, until the identity has its floor:
 *
 *   1. the screen's `lead` controls' tier (full → compact → overflow) — the
 *      deck screen's study buttons and daily switch shedding their labels;
 *   2. the screen's tools tier (full → compact → overflow), for a screen that
 *      publishes more than one;
 *   3. the return affordance's LABEL, leaving its chevron;
 *   4. with those spent, the name truncates past its floor, and at
 *      `SCREEN_HEADER_NAME_NARROW_FLOOR` the action gives its label
 *      (`actionCompact`), for a screen that offers one.
 *
 * Each rung records the row width the rung above it needs, and the row steps
 * back up only once it is that wide plus the hysteresis — see `upgradeAt`.
 * The floors are the NAME's: a chip beside it is taken off first.
 *
 * Rung 3 is the mechanism fix for the defect §8.4 records as found and not
 * holding: the ladder used to have only rung 1, so a screen publishing a
 * single tools tier — the deck screen — had nothing to step down to, and the
 * floor was breached in the last few pixels above the `md` breakpoint (182px
 * of identity at a 775px viewport, 176px at 769). The answer is not a smaller
 * floor or a made-up compact tier for one screen: it is that the row has a
 * second thing it can honestly give up. The back LABEL is the right one —
 * §10.1 makes the return affordance secondary to the name by design, its
 * destination is still reachable (the chevron stays, with its `aria-label`),
 * and the name is what the floor exists to protect.
 *
 * The tools zone is measured to decide its tier and rendered at that tier's
 * natural width (`flex-shrink: 0`). A ResizeObserver watches the row (and the
 * currently-rendered tools node, so a tier switch triggers its own recheck)
 * and nudges one step at a time toward the best fit — converging within a
 * couple of callbacks rather than probing every tier's width up front.
 */
export default function ScreenHeaderBar() {
  const config = useScreenHeaderStore((s) => s.config)
  const rowRef = useRef<HTMLDivElement>(null)
  const backRef = useRef<HTMLButtonElement>(null)
  const leadRef = useRef<HTMLDivElement>(null)
  const actionRef = useRef<HTMLDivElement>(null)
  const toolsRef = useRef<HTMLDivElement>(null)
  const chipRef = useRef<HTMLSpanElement>(null)
  const [leadTier, setLeadTier] = useState<ToolsTier>('full')
  const [tier, setTier] = useState<ToolsTier>('full')
  /** The last rung of the ladder: the return affordance keeps its chevron and
   *  drops its label once the controls have nothing left to give. */
  const [backLabelHidden, setBackLabelHidden] = useState(false)
  /** The final rung: the action at its icon (`actionCompact`), taken only
   *  when the name has come down to its narrow floor. */
  const [actionCompacted, setActionCompacted] = useState(false)
  // The rungs as last committed, read by the fit check. The check decides the
  // NEXT rung from the current one, and a state updater is the wrong place to
  // learn the current one: React may run it later, during render, so a flag
  // set inside it is not reliably set by the time the check reads it.
  const rungs = useRef({ leadTier, tier, backLabelHidden, actionCompacted })
  useLayoutEffect(() => {
    rungs.current = { leadTier, tier, backLabelHidden, actionCompacted }
  })
  /**
   * For each rung taken, the row width the rung above it needs — recorded at
   * the moment of stepping down (the width then, plus the identity's deficit
   * then). Stepping back up is decided against THAT, not against the identity
   * space the lower rung leaves: a rung can free far more than the hysteresis
   * (a study button's label is ~145px), and judged on freed space alone the
   * row stepped down, saw room, stepped up, saw none, and looped until React
   * gave up (measured at 1280px on the deck screen, 2026-09-23).
   */
  const upgradeAt = useRef<number[]>([])
  const contentRef = useRef<unknown[]>([])

  const tools = config?.tools ?? null
  const lead = config?.lead ?? null
  const hasCompact = !!tools?.compact
  const hasOverflow = !!tools?.overflow
  const hasLeadCompact = !!lead?.compact
  const hasLeadOverflow = !!lead?.overflow
  const hasBack = !!config?.back
  const action = config?.action ?? null
  const actionCompact = config?.actionCompact ?? null

  useLayoutEffect(() => {
    const row = rowRef.current
    if (!row) return

    // New content (another screen, or this screen's controls changing shape):
    // every recorded threshold was measured against other controls, so the
    // ladder starts again from the top rather than from a rung it can no
    // longer justify. All inside layout effects, so no intermediate rung paints.
    const content = [tools, lead, action, actionCompact, hasBack]
    if (content.some((c, i) => c !== contentRef.current[i])) {
      contentRef.current = content
      upgradeAt.current = []
      const cur = rungs.current
      if (cur.leadTier !== 'full' || cur.tier !== 'full' || cur.backLabelHidden || cur.actionCompacted) {
        setLeadTier('full')
        setTier('full')
        setBackLabelHidden(false)
        setActionCompacted(false)
        return
      }
    }

    const check = () => {
      const available = row.clientWidth
      const backW = backRef.current?.offsetWidth ?? 0
      const leadW = leadRef.current?.offsetWidth ?? 0
      const toolsW = toolsRef.current?.offsetWidth ?? 0
      // The action never degrades and never shrinks (§10.6 — it keeps the same
      // weight in both mode states), so it is measured with the rest of the
      // chrome: whatever it occupies is taken off what the identity has, which
      // is what steps the ladder down rather than clipping the action.
      const actionW = actionRef.current?.offsetWidth ?? 0
      // Two gaps around the slack when a control group renders, one when it
      // does not — plus the slack's own minimum, which is a real element and
      // takes its share of the row before the identity does. Inside the
      // controls group each further member costs its own gap.
      const members = (leadW ? 1 : 0) + (actionW ? 1 : 0) + (toolsW ? 1 : 0)
      const gaps = SCREEN_HEADER_GAP * ((hasBack ? 1 : 0) + (members ? 2 : 1))
        + SCREEN_HEADER_CONTROLS_GAP * Math.max(0, members - 1)
      // The floors are the NAME's, so the chip beside it (and its 8px gap) is
      // taken off the identity zone before comparing: counted as name, a
      // system deck's chip let its name fall ~70px below either floor before
      // any rung moved.
      const chipW = chipRef.current?.offsetWidth ?? 0
      const identitySpace = available - backW - leadW - actionW - toolsW - gaps - SCREEN_HEADER_ZONE_GAP
        - (chipW ? chipW + 8 : 0)
      const cur = rungs.current

      // Up first, against the width recorded when the rung was taken — never
      // against the space the lower rung happens to leave (see `upgradeAt`).
      // The last rung taken returns first; one rung per callback.
      const threshold = upgradeAt.current[upgradeAt.current.length - 1]
      if (threshold !== undefined && available >= threshold + SCREEN_HEADER_UPGRADE_HYSTERESIS) {
        upgradeAt.current.pop()
        if (cur.actionCompacted) setActionCompacted(false)
        else if (cur.backLabelHidden) setBackLabelHidden(false)
        else if (cur.tier === 'overflow') setTier(hasCompact ? 'compact' : 'full')
        else if (cur.tier === 'compact') setTier('full')
        else if (cur.leadTier === 'overflow') setLeadTier(hasLeadCompact ? 'compact' : 'full')
        else if (cur.leadTier === 'compact') setLeadTier('full')
        return
      }

      // Down, one rung per callback. The screen's lead controls spend their
      // labels first, then its tools degrade, and only then does the row take
      // anything from the return affordance, which is navigation. With those
      // spent the name truncates below its floor; at the narrow floor the
      // action gives its label, the last thing the bar has to give. Each step
      // records the row width the rung above it needs.
      const push = (floor: number) => upgradeAt.current.push(available + (floor - identitySpace))
      if (identitySpace < SCREEN_HEADER_NAME_MIN_WIDTH) {
        if (cur.leadTier === 'full' && (hasLeadCompact || hasLeadOverflow)) {
          setLeadTier(hasLeadCompact ? 'compact' : 'overflow'); push(SCREEN_HEADER_NAME_MIN_WIDTH); return
        }
        if (cur.leadTier === 'compact' && hasLeadOverflow) {
          setLeadTier('overflow'); push(SCREEN_HEADER_NAME_MIN_WIDTH); return
        }
        if (cur.tier === 'full' && (hasCompact || hasOverflow)) {
          setTier(hasCompact ? 'compact' : 'overflow'); push(SCREEN_HEADER_NAME_MIN_WIDTH); return
        }
        if (cur.tier === 'compact' && hasOverflow) {
          setTier('overflow'); push(SCREEN_HEADER_NAME_MIN_WIDTH); return
        }
        if (hasBack && !cur.backLabelHidden) {
          setBackLabelHidden(true); push(SCREEN_HEADER_NAME_MIN_WIDTH); return
        }
      }
      if (identitySpace < SCREEN_HEADER_NAME_NARROW_FLOOR && actionCompact && !cur.actionCompacted) {
        setActionCompacted(true); push(SCREEN_HEADER_NAME_NARROW_FLOOR)
      }
    }

    check()
    const ro = new ResizeObserver(check)
    ro.observe(row)
    if (leadRef.current) ro.observe(leadRef.current)
    if (toolsRef.current) ro.observe(toolsRef.current)
    if (actionRef.current) ro.observe(actionRef.current)
    if (backRef.current) ro.observe(backRef.current)
    return () => ro.disconnect()
    // Re-run whenever the published content changes identity (a different
    // screen, or the same screen's controls changing shape) or a rung moved —
    // the previously-measured rung may no longer be the right one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tools, lead, hasBack, hasCompact, hasOverflow, hasLeadCompact, hasLeadOverflow, action, actionCompact, backLabelHidden, actionCompacted, leadTier, tier])

  if (!config) {
    return (
      <header
        className="hidden md:flex"
        style={{
          height: 'var(--header-height)',
          flexShrink: 0,
          backgroundColor: 'var(--bg-screen-header)',
          borderBottom: '1px solid var(--border-chrome)',
        }}
      />
    )
  }

  const pickTier = (t: ScreenHeaderTools | null, at: ToolsTier) => !t ? null
    : at === 'overflow' ? (t.overflow ?? t.compact ?? t.full)
    : at === 'compact' ? (t.compact ?? t.full)
    : t.full
  const toolsNode = pickTier(tools, tier)
  const leadNode = pickTier(lead, leadTier)

  return (
    <header
      className="hidden md:flex items-center"
      style={{
        height: 'var(--header-height)',
        flexShrink: 0,
        backgroundColor: 'var(--bg-screen-header)',
        borderBottom: '1px solid var(--border-chrome)',
        padding: '0 24px',
        zIndex: 30,
      }}
    >
      <div
        ref={rowRef}
        style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, gap: SCREEN_HEADER_GAP }}
      >
        {/* ── The left end: identity ─────────────────────────────────────── */}
        {config.back && (
          <button
            ref={backRef}
            onClick={config.back.onClick}
            // The label is the ladder's second rung and can be away; the
            // destination must not be, so the accessible name is the label
            // whether or not it is painted.
            aria-label={config.back.label}
            style={{
              display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0,
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              color: 'var(--text-header-secondary)', fontSize: 12, fontWeight: 500,
              fontFamily: 'var(--font-ui)', whiteSpace: 'nowrap',
            }}
          >
            {config.back.icon === 'close' ? <X size={14} /> : <ChevronLeft size={14} />}
            {!backLabelHidden && config.back.label}
          </button>
        )}

        {/* `flex: 0 1 auto` — shrinks, down to the floor the ladder above
            defends, and never grows. Growing it is what the slack is for. */}
        <div
          // Stable hook a screen's own content can measure (currently only
          // Library, whose tabs and search align to the page's content column
          // rather than to wherever this zone happens to end — see
          // LibraryHeaderTools). Singleton element (one ScreenHeaderBar in the
          // whole app), so a fixed id is safe.
          id="screen-header-identity"
          title={config.titleHint ?? undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            flex: '0 1 auto', overflow: 'hidden',
          }}
        >
          {/* The name, and under it the screen's own facts — one column, both
              truncating, neither wrapping. Two lines inside the same bar
              height, which is what lets a screen move its facts up here
              without the bar growing (ADR-067, thirteenth amendment). */}
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
            <span
              data-screen-header-title
              style={{
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                fontSize: config.subtitle ? 16 : 18, fontWeight: 700, letterSpacing: '-0.01em',
                lineHeight: 1.25,
                color: 'var(--text-header)', fontFamily: 'var(--font-ui)',
              }}
            >
              {config.title}
            </span>
            {config.subtitle && (
              <span
                data-screen-header-subtitle
                style={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontSize: 12, lineHeight: 1.35, marginTop: 1,
                  color: 'var(--text-header-secondary)', fontFamily: 'var(--font-ui)',
                }}
              >
                {config.subtitle}
              </span>
            )}
          </div>
          {/* A chip is part of the identity — a state label about the screen,
              never a control — which is why it is the one thing allowed after
              the name. §10.1's prohibition is on a CONTROL sitting there. */}
          {config.chip && (
            <span
              ref={chipRef}
              style={{
                flexShrink: 0, fontSize: 11, fontWeight: 600, padding: '2px 8px',
                borderRadius: 'var(--radius-pill)', border: '1px solid var(--border-chrome)',
                color: 'var(--text-header-secondary)', fontFamily: 'var(--font-ui)',
                textTransform: 'uppercase' as const, letterSpacing: '0.04em',
              }}
            >
              {config.chip}
            </span>
          )}
        </div>

        {/* ── The slack: the only thing that stretches ────────────────────
            A screen's own navigation centres in it; a screen without
            navigation leaves it empty (and it is then pure space, which is
            what holds the two ends apart). `--header-zone-gap` is its
            minimum, so the identity and the controls never touch even at the
            width where the identity is at its floor.

            Library's navigation is `position: fixed` and aligned to the page's
            content column, so it draws OVER this zone rather than laying out
            inside it. That is not an exception to the model — the content
            column's right edge is the alignment that pass derived, and this
            zone is where a field consumes the slack (§10.7). What this element
            gives it is the space: without a real slack the free width sat at
            the right group's `margin-left: auto`, where nothing could be put
            in it. */}
        <div style={{
          flex: '1 1 auto', minWidth: SCREEN_HEADER_ZONE_GAP,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          alignSelf: 'stretch', minHeight: 0,
        }}>
          {config.nav}
        </div>

        {/* ── The right end: the screen's controls, one group ─────────────
            Action then overflow, least-persistent first. `flexShrink: 0`
            throughout: this group is measured by the ladder above and steps
            DOWN a tier rather than being squeezed, and the action never
            degrades at all (§10.6 — a control must not recede when it is off).
            The group reaches the bar's own right inset directly; see the
            header comment for why no hairline closes it. */}
        {(leadNode || action || toolsNode) && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: SCREEN_HEADER_CONTROLS_GAP, flexShrink: 0,
          }}>
            {/* The screen's lead controls, ahead of the action — stepped
                down first when the row is tight (see the fit check). */}
            {leadNode && (
              <div ref={leadRef} data-screen-header-lead style={{ display: 'flex', alignItems: 'center', gap: SCREEN_HEADER_CONTROLS_GAP, flexShrink: 0 }}>
                {leadNode}
              </div>
            )}
            {action && (
              <div ref={actionRef} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {actionCompacted && actionCompact ? actionCompact : action}
              </div>
            )}
            {toolsNode && (
              <div ref={toolsRef} style={{ display: 'flex', alignItems: 'center', gap: SCREEN_HEADER_CONTROLS_GAP, flexShrink: 0, position: 'relative' }}>
                {toolsNode}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
